import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Button,
  Input,
  message,
  Empty,
  Tag,
  Tooltip,
  Spin,
} from "antd";
import {
  SendOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  MessageOutlined,
  ToolOutlined,
  CalendarOutlined,
  BuildOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import PageHeader from "../components/PageHeader";
import { device } from "../lib/breakpoints";
import chatModel from "../lib/firebase/chatModel";
import { chatKeys } from "../lib/queryKeys";
import { getAuth } from "firebase/auth";
import { trackEvent } from "../helpers/mixpanel";
import { acceptBid, rejectBid } from "../lib/api/vendorBidApi";

const { TextArea } = Input;

const VendorChatDashboard = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedChat, setSelectedChat] = useState(null);
  const [acceptingBidId, setAcceptingBidId] = useState(null);
  const [rejectingBidId, setRejectingBidId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastIsAtBottomRef = useRef(true);
  const prevMessagesCountRef = useRef(0);
  const unsubscribeRef = useRef(null);

  // Debug mode for vendor chat
  const isDebugVendorChat = (() => {
    if (typeof window === "undefined") return false;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToggle = urlParams.get("debug");
      const lsToggle = localStorage.getItem("debug-vendor-chat") === "true";
      const active =
        lsToggle || urlToggle === "vendor-chat" || urlToggle === "true";
      if (active) {
        console.log("[VendorChat] Debug mode active: vendor-chat");
      }
      return active;
    } catch (e) {
      return false;
    }
  })();

  // Debug vendor chats (similar to tenant chat)
  const debugVendorChats = isDebugVendorChat
    ? [
        {
          id: "debug-vendor-1",
          vendorId: "vendor-1",
          vendorName: "ABC Plumbing",
          vendorEmail: "contact@abcplumbing.com",
          vendorPhone: "(555) 123-4567",
          specialties: ["plumbing", "hvac"],
          lastMessage:
            "I can come by tomorrow morning between 9-11am. Does that work?",
          lastMessageTime: "2 hours ago",
          unreadCount: 1,
          repairId: "repair-1",
          repairTitle: "Kitchen sink leak",
          bidId: "bid-1",
          bidAmount: 350,
          bidStatus: "pending",
        },
        {
          id: "debug-vendor-2",
          vendorId: "vendor-2",
          vendorName: "Quick Fix Electrical",
          vendorEmail: "info@quickfixelectrical.com",
          vendorPhone: "(555) 987-6543",
          specialties: ["electrical"],
          lastMessage:
            "Thanks for accepting my bid. I'll send an ETA text when I'm on the way.",
          lastMessageTime: "1 day ago",
          unreadCount: 0,
          repairId: "repair-2",
          repairTitle: "Faulty outlet in living room",
          bidId: "bid-2",
          bidAmount: 425,
          bidStatus: "accepted",
        },
      ]
    : [];

  // Debug messages
  const debugMessages =
    isDebugVendorChat && selectedChat?.id === "debug-vendor-1"
      ? [
          {
            id: "m1",
            sender: "vendor",
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
            content:
              "Hi, I saw the bid request for the kitchen sink leak. I can take a look at it.",
          },
          {
            id: "m2",
            sender: "landlord",
            timestamp: new Date(
              Date.now() - 1000 * 60 * 60 * 4.8
            ).toISOString(),
            content: "Thanks for your interest. When would you be available?",
          },
          {
            id: "m3",
            sender: "vendor",
            timestamp: new Date(
              Date.now() - 1000 * 60 * 60 * 4.5
            ).toISOString(),
            content:
              "I can come by tomorrow morning between 9-11am. Does that work?",
          },
          {
            id: "m4",
            sender: "landlord",
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
            content:
              "That works perfectly. I'll send you the unit address and access instructions.",
          },
        ]
      : [];

  // Fetch vendor chats (would be replaced with real API call)
  const { data: vendorChats = [], isLoading: isLoadingChats } = useQuery({
    queryKey: ["vendorChats", user?.uid],
    queryFn: async () => {
      if (isDebugVendorChat) {
        return debugVendorChats;
      }
      // TODO: Replace with real API call
      // const apiUrl = process.env.REACT_APP_API_URL;
      // const token = await getAuth().currentUser?.getIdToken();
      // const response = await fetch(`${apiUrl}/vendor-chats`, {
      //   headers: { 'Authorization': `Bearer ${token}` }
      // });
      // return response.json();
      return [];
    },
    enabled: !!user?.uid || isDebugVendorChat,
  });

  // Load messages for selected chat
  const {
    data: messages = [],
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: chatKeys.messages(selectedChat?.id),
    queryFn: () => chatModel.getMessages(selectedChat.id),
    enabled: !!selectedChat?.id && !isDebugVendorChat,
    refetchInterval: isDebugVendorChat ? undefined : 5000,
  });

  const messagesToRender =
    isDebugVendorChat && selectedChat ? debugMessages : messages;

  // Track scroll position
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const nearBottom = scrollHeight - scrollTop - clientHeight <= 5;
      if (lastIsAtBottomRef.current !== nearBottom) {
        console.log("[VendorChat] scroll-state-change", {
          isAtBottom: nearBottom,
          scrollTop,
          scrollHeight,
          clientHeight,
          timestamp: new Date().toISOString(),
        });
        lastIsAtBottomRef.current = nearBottom;
      }
      setIsAtBottom(nearBottom);
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll only if at bottom
  useEffect(() => {
    const newCount = messagesToRender.length;
    const prevCount = prevMessagesCountRef.current;
    const hasNew = newCount > prevCount;
    if (hasNew) {
      console.log("[VendorChat] new-messages", {
        previousCount: prevCount,
        newCount,
        isAtBottom,
        action: isAtBottom ? "auto-scroll" : "skip",
        timestamp: new Date().toISOString(),
      });
      if (isAtBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevMessagesCountRef.current = newCount;
  }, [messagesToRender.length, isAtBottom]);

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp?.toDate?.() || new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;

    try {
      setIsComposing(true);

      trackEvent("landlord_vendor_message_sent", {
        chatId: selectedChat.id,
        vendorId: selectedChat.vendorId,
      });

      if (isDebugVendorChat) {
        // Simulate message in debug mode
        message.success("Message sent (debug mode)");
        setNewMessage("");
        return;
      }

      await chatModel.addMessage(selectedChat.id, "landlord", newMessage);
      await queryClient.invalidateQueries({
        queryKey: chatKeys.messages(selectedChat.id),
      });

      setNewMessage("");
      message.success("Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      message.error("Failed to send message");
    } finally {
      setIsComposing(false);
    }
  };

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);

    trackEvent("vendor_chat_selected", {
      chatId: chat.id,
      vendorId: chat.vendorId,
    });
  };

  const handleAcceptBid = async (bidId) => {
    if (!bidId || !selectedChat) return;
    setAcceptingBidId(bidId);
    try {
      const result = await acceptBid(bidId, {
        ticketId: selectedChat.repairId ?? selectedChat.ticketId,
        workOrderId: selectedChat.workOrderId,
        landlordId: user?.uid,
      });
      await queryClient.invalidateQueries({
        queryKey: ["vendorChats", user?.uid],
      });
      message.success(
        result?.workOrderId
          ? "Bid accepted. Approve the work order in Contractor Bids to notify the vendor."
          : "Bid accepted."
      );
      trackEvent("vendor_bid_accepted", {
        bidId,
        ticketId: result?.ticketId,
        workOrderId: result?.workOrderId,
      });
      navigate("/bid/success", {
        state: {
          ticketId: selectedChat.repairId ?? result?.ticketId,
          workOrderId: result?.workOrderId ?? selectedChat.repairId,
          ticketTitle: selectedChat.repairTitle,
          bidAmount: selectedChat.bidAmount,
        },
      });
    } catch (err) {
      console.error("[VendorChatDashboard] acceptBid failed:", err);
      message.error(err?.message || "Failed to accept bid. Please try again.");
    } finally {
      setAcceptingBidId(null);
    }
  };

  const handleRejectBid = async (bidId) => {
    if (!bidId) return;
    setRejectingBidId(bidId);
    try {
      await rejectBid(bidId);
      await queryClient.invalidateQueries({
        queryKey: ["vendorChats", user?.uid],
      });
      message.success("Bid rejected.");
      setSelectedChat((prev) => (prev?.bidId === bidId ? null : prev));
      trackEvent("vendor_bid_rejected", { bidId });
    } catch (err) {
      console.error("[VendorChatDashboard] rejectBid failed:", err);
      message.error(err?.message || "Failed to reject bid. Please try again.");
    } finally {
      setRejectingBidId(null);
    }
  };

  if (isLoadingChats) {
    return (
      <Container>
        <PageHeader
          title="Vendor Chat"
          subtitle="Communicate with contractors about repair bids"
          icon={MessageOutlined}
        />
        <LoadingContainer>
          <Spin size="large" />
          <LoadingText>Loading vendor chats...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title="Vendor Chat"
        subtitle="Communicate with contractors about repair bids"
        icon={MessageOutlined}
      />

      <DashboardLayout>
        {/* Vendor Chat List */}
        <ChatSidebar>
          <SidebarHeader>Vendors</SidebarHeader>
          {vendorChats.length === 0 ? (
            <Empty
              description="No vendor chats yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <ChatList>
              {vendorChats.map((chat) => (
                <ChatItem
                  key={chat.id}
                  $isSelected={selectedChat?.id === chat.id}
                  onClick={() => handleChatSelect(chat)}
                >
                  <VendorAvatar>
                    <Avatar
                      size="large"
                      icon={<BuildOutlined />}
                      style={{ backgroundColor: "#52c41a" }}
                    />
                    {chat.unreadCount > 0 && (
                      <UnreadBadge>{chat.unreadCount}</UnreadBadge>
                    )}
                  </VendorAvatar>
                  <ChatInfo>
                    <VendorName>{chat.vendorName}</VendorName>
                    <RepairTitle>{chat.repairTitle}</RepairTitle>
                    <LastMessage>{chat.lastMessage}</LastMessage>
                    <LastMessageTime>{chat.lastMessageTime}</LastMessageTime>
                  </ChatInfo>
                  {chat.bidStatus === "pending" && (
                    <BidBadge $status="pending">${chat.bidAmount}</BidBadge>
                  )}
                </ChatItem>
              ))}
            </ChatList>
          )}
        </ChatSidebar>

        {/* Chat Area */}
        <ChatArea>
          {!selectedChat ? (
            <EmptyState>
              <MessageOutlined
                style={{ fontSize: 48, color: "#d9d9d9", marginBottom: "1rem" }}
              />
              <EmptyText>Select a vendor to start chatting</EmptyText>
            </EmptyState>
          ) : (
            <>
              {/* Chat Header */}
              <ChatHeader>
                <VendorInfo>
                  <Avatar
                    size="large"
                    icon={<BuildOutlined />}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                  <VendorDetails>
                    <VendorNameHeader>
                      {selectedChat.vendorName}
                    </VendorNameHeader>
                    <VendorMeta>
                      <MetaItem>
                        <MailOutlined /> {selectedChat.vendorEmail}
                      </MetaItem>
                      <MetaItem>
                        <PhoneOutlined /> {selectedChat.vendorPhone}
                      </MetaItem>
                      <MetaItem>
                        <ToolOutlined /> {selectedChat.specialties?.join(", ")}
                      </MetaItem>
                    </VendorMeta>
                    {selectedChat.repairTitle && (
                      <RepairInfo>
                        <RepairLabel>Repair:</RepairLabel>{" "}
                        {selectedChat.repairTitle}
                      </RepairInfo>
                    )}
                  </VendorDetails>
                </VendorInfo>
                {selectedChat.bidStatus === "pending" && (
                  <BidActions>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      loading={acceptingBidId === selectedChat.bidId}
                      disabled={!!acceptingBidId || !!rejectingBidId}
                      onClick={() => handleAcceptBid(selectedChat.bidId)}
                    >
                      Accept Bid (${selectedChat.bidAmount})
                    </Button>
                    <Button
                      icon={<CloseCircleOutlined />}
                      loading={rejectingBidId === selectedChat.bidId}
                      disabled={!!acceptingBidId || !!rejectingBidId}
                      onClick={() => handleRejectBid(selectedChat.bidId)}
                    >
                      Reject
                    </Button>
                  </BidActions>
                )}
              </ChatHeader>

              {/* Messages */}
              <MessagesContainer ref={messagesContainerRef}>
                {isLoadingMessages ? (
                  <LoadingContainer>
                    <Spin size="large" />
                    <LoadingText>Loading messages...</LoadingText>
                  </LoadingContainer>
                ) : messagesToRender.length === 0 ? (
                  <EmptyState>
                    <EmptyText>
                      No messages yet. Start the conversation!
                    </EmptyText>
                  </EmptyState>
                ) : (
                  messagesToRender.map((msg) => (
                    <MessageBubble key={msg.id} $sender={msg.sender}>
                      <MessageAvatar $sender={msg.sender}>
                        <Avatar
                          size="small"
                          icon={
                            msg.sender === "landlord" ? (
                              <UserOutlined />
                            ) : (
                              <BuildOutlined />
                            )
                          }
                          style={{
                            backgroundColor:
                              msg.sender === "landlord"
                                ? "var(--color-primary)"
                                : "#52c41a",
                          }}
                        />
                      </MessageAvatar>
                      <MessageContent $sender={msg.sender}>
                        <MessageHeader $sender={msg.sender}>
                          <MessageSender>
                            {msg.sender === "landlord"
                              ? "You"
                              : selectedChat?.vendorName}
                          </MessageSender>
                          <MessageTime>{formatTime(msg.timestamp)}</MessageTime>
                        </MessageHeader>
                        {msg.content && String(msg.content).trim() && (
                          <MessageText $sender={msg.sender}>
                            {msg.content}
                          </MessageText>
                        )}
                      </MessageContent>
                    </MessageBubble>
                  ))
                )}
                <div ref={messagesEndRef} />
              </MessagesContainer>

              {/* Input Area */}
              <InputArea>
                <InputContainer>
                  <TextArea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Message ${
                      selectedChat?.vendorName || "vendor"
                    }...`}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <SendButton
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    loading={isComposing}
                    disabled={!newMessage.trim()}
                  >
                    Send
                  </SendButton>
                </InputContainer>
              </InputArea>
            </>
          )}
        </ChatArea>
      </DashboardLayout>
    </Container>
  );
};

export default VendorChatDashboard;

// Styled Components
const Container = styled.div`
  padding: 2rem;
  background: var(--color-bg-primary);
  min-height: 100vh;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
`;

const LoadingText = styled.div`
  color: var(--color-text-light);
  font-family: var(--font-secondary);
`;

const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 2rem;
  margin-top: 2rem;
  height: calc(100vh - 200px);
  min-height: 600px;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    height: auto;
    min-height: auto;
  }
`;

const ChatSidebar = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media ${device.mobile} {
    max-height: 300px;
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-primary);
`;

const ChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
`;

const ChatItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$isSelected ? "#f0f7ff" : "transparent")};
  border: 1px solid
    ${(props) => (props.$isSelected ? "#1890ff" : "transparent")};

  &:hover {
    background: ${(props) => (props.$isSelected ? "#f0f7ff" : "#fafafa")};
  }
`;

const VendorAvatar = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const UnreadBadge = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ff4d4f;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
`;

const ChatInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const VendorName = styled.div`
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-secondary);
  margin-bottom: 0.25rem;
`;

const RepairTitle = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
  margin-bottom: 0.5rem;
`;

const LastMessage = styled.div`
  font-size: 0.9rem;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
`;

const LastMessageTime = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-light);
`;

const BidBadge = styled(Tag)`
  flex-shrink: 0;
  margin-top: 0.5rem;
`;

const ChatArea = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
`;

const EmptyText = styled.div`
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  font-size: 1rem;
`;

const ChatHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const VendorInfo = styled.div`
  display: flex;
  gap: 1rem;
  flex: 1;
`;

const VendorDetails = styled.div`
  flex: 1;
`;

const VendorNameHeader = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-primary);
  margin-bottom: 0.5rem;
`;

const VendorMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const MetaItem = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-light);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RepairInfo = styled.div`
  font-size: 0.9rem;
  color: var(--color-text);
  margin-top: 0.5rem;
`;

const RepairLabel = styled.span`
  font-weight: 600;
  color: var(--color-text-light);
`;

const BidActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 3px;
  }
`;

const MessageBubble = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-direction: ${(props) =>
    props.$sender === "landlord" ? "row-reverse" : "row"};
`;

const MessageAvatar = styled.div`
  flex-shrink: 0;
`;

const MessageContent = styled.div`
  max-width: 70%;
  display: flex;
  flex-direction: column;
  align-items: ${(props) =>
    props.$sender === "landlord" ? "flex-end" : "flex-start"};
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  flex-direction: ${(props) =>
    props.$sender === "landlord" ? "row-reverse" : "row"};
`;

const MessageSender = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--color-text);
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-light);
`;

const MessageText = styled.div`
  background: ${(props) =>
    props.$sender === "landlord" ? "var(--color-primary)" : "#f0f0f0"};
  color: ${(props) =>
    props.$sender === "landlord" ? "white" : "var(--color-text)"};
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const InputArea = styled.div`
  border-top: 1px solid #f0f0f0;
  padding: 1rem 1.5rem;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const SendButton = styled(Button)`
  flex-shrink: 0;
  height: auto;
`;
